import { LineBuilder, type Rgba } from '../gfx/geometry.ts';
import { LineRenderer, type DrawCall, type GpuBatch } from '../gfx/renderer.ts';
import { OrbitCamera } from '../gfx/orbit.ts';
import { project } from '../gfx/mat4.ts';
import { gridIndex, voxelCentre } from '../dsp/backproject.ts';
import type { Reconstruction } from '../dsp/reconstruct.ts';
import type { FloorPlan } from '../dsp/planes.ts';

/**
 * The reconstructed room, drawn.
 *
 * The single-measurement view draws spheres because that is all one chirp
 * knows: a radius, and no bearing whatsoever. This view is allowed to draw
 * walls in places because a scan measured where they are - but the drawing
 * still has to distinguish the three quite different kinds of confidence it
 * is now carrying, or it goes straight back to overstating itself.
 *
 *   Fitted surfaces are drawn as panels standing between the floor and the
 *   ceiling, one straight panel per wall.
 *
 *   Outline the scan never closed is dashed and stays on the ground. Where no
 *   wall stood clear of the reverberation, the room is carried at the furthest
 *   wall that *was* found and marked as assumption rather than measurement.
 *
 *   Objects are drawn as boxes, faded by how well the scan surrounded them.
 *   Their footprints are far better known than their heights, because a phone
 *   turning about a vertical axis gets plenty of parallax horizontally and
 *   almost none vertically, so a box that looks tall is usually a smear along
 *   the line of sight rather than something tall.
 *
 * The voxel volume is on by default and graded by hue. It is the rawest thing
 * here - what the scan measured, before any surface was fitted to it - and it
 * was once hidden because at a flat 7% ink it turned the room into a fog of
 * identical dots. Opacity was the wrong channel to carry strength in: it is
 * exactly the one that vanishes against dark paper. Grading hot cells towards
 * the accent instead means the strong returns read as strong in either theme,
 * the weak ones stay out of the way, and the volume can be the first thing you
 * see rather than something to go looking for.
 */

export interface MapViewHandle {
  update(reconstruction: Reconstruction): void;
  setCloudVisible(visible: boolean): void;
  /** Cells drawn, and cells over the threshold, so the thinning is reportable. */
  cloudCounts(): { shown: number; total: number };
  destroy(): void;
}

interface Marker {
  element: HTMLElement;
  at: [number, number, number];
}

interface WallRun {
  a: [number, number];
  b: [number, number];
  /** Index into the plan's wall list, or -1 where the outline is a guess. */
  wall: number;
}

/**
 * One run of the outline that rides a single wall, as a straight segment.
 *
 * The outline is sampled every two degrees, so a flat wall arrives as thirty
 * consecutive vertices collinear to within a millimetre. Drawing them as
 * thirty segments is part of what made a rectangular room read as a soft blob:
 * every joint is a place for a hairline to break, and the eye sums the result
 * into a curve. One segment per wall puts the corners back.
 */
function wallRuns(plan: FloorPlan): WallRun[] {
  const outline = plan.outline;
  const n = outline.length;
  if (n === 0) return [];
  const runs: WallRun[] = [];

  // Start at a change of wall, so the run that straddles bearing zero is not
  // split into two halves that then draw as two separate walls.
  let origin = 0;
  for (let i = 0; i < n; i++) {
    if (plan.wallIndex[i] !== plan.wallIndex[(i + n - 1) % n]) {
      origin = i;
      break;
    }
  }

  let startIndex = origin;
  for (let step = 1; step <= n; step++) {
    const i = (origin + step) % n;
    const previous = (i + n - 1) % n;
    if (plan.wallIndex[i] === plan.wallIndex[previous] && step < n) continue;
    const from = outline[startIndex];
    const to = outline[previous];
    runs.push({ a: [from.x, from.z], b: [to.x, to.z], wall: plan.wallIndex[previous] });
    startIndex = i;
  }
  return runs;
}

export async function createMapView(
  canvas: HTMLCanvasElement,
  overlay: HTMLElement,
): Promise<MapViewHandle> {
  const renderer = await LineRenderer.create(canvas);

  let current: Reconstruction | null = null;
  let structure: GpuBatch | null = null;
  let cloud: GpuBatch | null = null;
  let showCloud = true;
  let cloudShown = 0;
  let cloudTotal = 0;
  let markers: Marker[] = [];
  let frame = 0;
  let onScreen = true;

  const camera = new OrbitCamera(canvas, { distance: 9, minDistance: 1.2, maxDistance: 60 }, () => {});

  const palette = (): { ink: Rgba; faint: Rgba; accent: Rgba; clear: [number, number, number, number] } => {
    const style = getComputedStyle(document.documentElement);
    const read = (name: string, fallback: string): Rgba =>
      parseColour(style.getPropertyValue(name).trim() || fallback);
    const paper = parseColour(style.getPropertyValue('--paper').trim() || '#faf9f6');
    return {
      ink: read('--ink', '#111111'),
      faint: read('--ink-faint', 'rgba(17,17,17,0.38)'),
      accent: read('--accent', '#e3000f'),
      clear: [paper[0], paper[1], paper[2], 1],
    };
  };

  function rebuild(next: Reconstruction): void {
    current = next;
    const colours = palette();
    const builder = new LineBuilder();
    const plan = next.floorPlan;

    const floorPlane = next.planes.find((p) => p.kind === 'floor');
    const ceilingPlane = next.planes.find((p) => p.kind === 'ceiling');
    const floorY = floorPlane ? -floorPlane.offsetM : -1.35;
    const ceilingY = ceilingPlane
      ? ceilingPlane.offsetM
      : Number.isFinite(plan.heightM)
        ? floorY + plan.heightM
        : floorY + 2.4;

    let extent = 2.5;
    for (const point of plan.outline) extent = Math.max(extent, Math.hypot(point.x, point.z));

    builder.grid(Math.ceil(extent), 1, alpha(colours.ink, 0.07), floorY);

    // The room. A measured wall is a panel standing between the floor and the
    // ceiling, because that is the claim being made - not a line on the ground
    // with the height left to the imagination. Where the scan found nothing,
    // the floor line is dashed and no panel goes up, so at a glance the two
    // are never confusable.
    const runs = wallRuns(plan);
    for (const run of runs) {
      if (Math.hypot(run.b[0] - run.a[0], run.b[1] - run.a[1]) < 1e-3) continue;
      if (run.wall >= 0) {
        const rungs = Math.max(2, Math.round((ceilingY - floorY) / 0.55));
        builder.wallPanel(run.a, run.b, floorY, ceilingY, alpha(colours.ink, 0.92), rungs);
      } else {
        builder.dashedPolyline(
          [
            [run.a[0], floorY, run.a[1]],
            [run.b[0], floorY, run.b[1]],
          ],
          alpha(colours.faint, 0.6),
          0.16,
          0.14,
        );
      }
    }

    // Floor and ceiling as the outline itself, closed - not as circles, which
    // suggest a round room nobody measured. The ceiling line is drawn fainter
    // still when its height was assumed rather than found.
    builder.polyline(
      plan.outline.map((p) => [p.x, floorY, p.z] as [number, number, number]),
      alpha(colours.ink, 0.5),
      true,
    );
    if (Number.isFinite(ceilingY)) {
      builder.polyline(
        plan.outline.map((p) => [p.x, ceilingY, p.z] as [number, number, number]),
        alpha(colours.ink, ceilingPlane ? 0.34 : 0.15),
        true,
      );
    }

    // The phone, and the blind zone the direct blast masks.
    builder.cross(0.16, alpha(colours.ink, 0.9));
    builder.circle(0.45, 'y', floorY + 0.005, alpha(colours.accent, 0.35), 72);

    for (const object of next.objects) {
      // Confidence is drawn, not written: a box the scan barely surrounded is
      // faint, one it circled properly is solid.
      const strength = 0.45 + 0.5 * Math.min(1, object.support / 0.05);
      builder.orientedBox(object.centre, object.half, object.yawRad, alpha(colours.accent, strength));
      // A tie down to the floor, so a box whose underside was never measured
      // reads as a height above the floor rather than as a thing hovering.
      builder.dashedPolyline(
        [
          [object.centre[0], object.centre[1] - object.half[1], object.centre[2]],
          [object.centre[0], floorY, object.centre[2]],
        ],
        alpha(colours.faint, 0.4),
        0.06,
        0.06,
      );
    }

    structure?.destroy();
    structure = renderer.upload(builder.build(), 'map-structure');

    cloud?.destroy();
    const built = buildCloud(next, colours);
    cloudShown = built.shown;
    cloudTotal = built.total;
    cloud = renderer.upload(built.batch, 'map-cloud');

    for (const marker of markers) marker.element.remove();
    markers = [];

    // Sizes, not names. The dimensions are measured; the noun is a guess made
    // from the dimensions, and hanging that guess on the drawing in bold puts
    // the weakest claim in the largest type. The guess belongs in the data
    // below, next to the confidence that goes with it.
    for (const object of next.objects) {
      const element = document.createElement('span');
      element.className = 'room-label';
      element.textContent = `${(object.half[0] * 2).toFixed(2)} × ${(object.half[2] * 2).toFixed(2)} m`;
      element.setAttribute('aria-hidden', 'true');
      overlay.append(element);
      markers.push({
        element,
        at: [object.centre[0], object.centre[1] + object.half[1] + 0.08, object.centre[2]],
      });
    }

    // One distance per wall, sitting on the wall rather than out along its
    // normal, so the number is where the surface it describes is.
    for (const run of runs) {
      if (run.wall < 0) continue;
      const wall = plan.walls[run.wall];
      if (!wall) continue;
      const element = document.createElement('span');
      element.className = 'room-label is-weak';
      element.textContent = `${wall.offsetM.toFixed(2)} m`;
      element.setAttribute('aria-hidden', 'true');
      overlay.append(element);
      markers.push({
        element,
        at: [
          (run.a[0] + run.b[0]) / 2,
          floorY + (ceilingY - floorY) * 0.62,
          (run.a[1] + run.b[1]) / 2,
        ],
      });
    }

    camera.reframe(extent * 2.6, { min: extent * 0.4, max: extent * 9 }, (floorY + ceilingY) / 2);
  }

  /**
   * The volume itself, as one tick per cell that stands clear of the
   * reverberation. This is the part of the picture that is not a model of
   * anything - it is what the scan measured, before any surface was fitted to
   * it or any box drawn round it.
   *
   * Off by default, and faint when on, because it was swamping the room. A
   * live room puts eight or nine thousand cells over the threshold; at three
   * hairlines each that is more marks than the walls, the floor grid and the
   * furniture put together, and the result read as a fog of dots with a room
   * somewhere inside it. So the ticks are budgeted - and thinned evenly across
   * the whole volume rather than by stopping at a cap, because taking the
   * first N cells in scan order fills the budget from the bottom of the room
   * and stops, which is not a subsample of the volume, it is a slice of it.
   */
  function buildCloud(
    next: Reconstruction,
    colours: ReturnType<typeof palette>,
  ): { batch: ReturnType<LineBuilder['build']>; shown: number; total: number } {
    const builder = new LineBuilder();
    const grid = next.grid;
    const threshold = 4.5;
    const budget = 7000;

    let total = 0;
    let peak = threshold;
    for (let i = 0; i < grid.valuesDb.length; i++) {
      if (grid.coverage[i] <= 0) continue;
      const value = grid.valuesDb[i];
      if (value < threshold) continue;
      total++;
      if (value > peak) peak = value;
    }

    const keep = total > budget ? budget / total : 1;
    const span = Math.max(1e-6, peak - threshold);
    let shown = 0;
    let carry = 0;

    for (let iy = 0; iy < grid.ny; iy++) {
      for (let iz = 0; iz < grid.nz; iz++) {
        for (let ix = 0; ix < grid.nx; ix++) {
          const index = gridIndex(grid, ix, iy, iz);
          if (grid.coverage[index] <= 0) continue;
          const value = grid.valuesDb[index];
          if (value < threshold) continue;
          carry += keep;
          if (carry < 1) continue;
          carry -= 1;
          const p = voxelCentre(grid, ix, iy, iz);
          const strength = Math.min(1, (value - threshold) / span);
          // Hot cells go red, cold cells stay ink. Strength used to be carried
          // by opacity alone, which is the one channel that disappears against
          // a dark background - a 7% white tick on near-black paper is nothing.
          // Hue survives both themes, so the strong returns read as strong
          // wherever the page is being looked at.
          const tint = mix(colours.ink, colours.accent, strength * strength);
          builder.tick(
            p[0],
            p[1],
            p[2],
            grid.stepM * (0.34 + 0.22 * strength),
            alpha(tint, 0.3 + 0.62 * strength),
          );
          shown++;
        }
      }
    }
    return { batch: builder.build(), shown, total };
  }

  function draw(): void {
    frame = requestAnimationFrame(draw);
    if (!current || !structure || !onScreen) return;
    const { width, height } = renderer.resize();
    const colours = palette();
    const viewProjection = camera.viewProjection(width / Math.max(1, height));

    // Volume first, room over it. Depth testing is off by design, so the order
    // these are submitted in *is* the stacking order, and the measured surfaces
    // have to come out on top of the raw cells rather than under them.
    const draws: DrawCall[] = [];
    if (cloud && showCloud) draws.push({ batch: cloud, scale: 1, alpha: 1 });
    draws.push({ batch: structure, scale: 1, alpha: 1 });

    renderer.render(viewProjection, draws, {
      clear: colours.clear,
      fadeNear: camera.distance * 0.35,
      fadeFar: camera.distance * 2.4,
    });

    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const cssWidth = width / ratio;
    const cssHeight = height / ratio;
    for (const marker of markers) {
      const point = project(viewProjection, marker.at);
      if (point.w <= 0) {
        marker.element.style.opacity = '0';
        continue;
      }
      const x = (point.x * 0.5 + 0.5) * cssWidth;
      const y = (1 - (point.y * 0.5 + 0.5)) * cssHeight;
      const visible = x > -60 && x < cssWidth + 60 && y > -30 && y < cssHeight + 30;
      marker.element.style.opacity = visible ? '1' : '0';
      marker.element.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`;
    }
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) onScreen = entry.isIntersecting;
    },
    { threshold: 0.01 },
  );
  observer.observe(canvas);
  frame = requestAnimationFrame(draw);

  return {
    update: rebuild,
    setCloudVisible(visible: boolean): void {
      showCloud = visible;
    },
    cloudCounts: () => ({ shown: cloudShown, total: cloudTotal }),
    destroy(): void {
      cancelAnimationFrame(frame);
      observer.disconnect();
      camera.destroy();
      for (const marker of markers) marker.element.remove();
      structure?.destroy();
      cloud?.destroy();
      renderer.destroy();
    },
  };
}

function alpha(colour: Rgba, a: number): Rgba {
  return [colour[0], colour[1], colour[2], colour[3] * a];
}

/** Blend two colours, for grading the cloud by how far a cell stands clear. */
function mix(a: Rgba, b: Rgba, t: number): Rgba {
  const k = Math.max(0, Math.min(1, t));
  return [
    a[0] + (b[0] - a[0]) * k,
    a[1] + (b[1] - a[1]) * k,
    a[2] + (b[2] - a[2]) * k,
    a[3] + (b[3] - a[3]) * k,
  ];
}

function parseColour(input: string): Rgba {
  const text = input.trim();
  if (text.startsWith('#')) {
    const hex = text.slice(1);
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex.padEnd(6, '0').slice(0, 6);
    return [
      parseInt(full.slice(0, 2), 16) / 255,
      parseInt(full.slice(2, 4), 16) / 255,
      parseInt(full.slice(4, 6), 16) / 255,
      1,
    ];
  }
  const match = text.match(/rgba?\(([^)]+)\)/);
  if (match) {
    const parts = match[1].split(/[,/\s]+/).filter(Boolean).map(Number);
    return [
      (parts[0] ?? 0) / 255,
      (parts[1] ?? 0) / 255,
      (parts[2] ?? 0) / 255,
      parts[3] === undefined ? 1 : parts[3],
    ];
  }
  return [0, 0, 0, 1];
}
